import React, { useCallback, useState } from 'react';
import classnames from 'classnames';

import { Aggregation } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { Svg } from 'src/components/Svg/Svg';
import { getSwatchData, swatchCss, SwatchData } from 'src/components/ProductDetail/utils/swatchData';
import { Button } from 'src/components/Button/Button';

import classes from './AggregationItem.scss';
import { OPTION_LABELS, SWATCH_ATTRIBUTE_CODES } from './layeredNav.types';

type AggregationItemProps = {
    item: Aggregation;
    onChange(checked: boolean, code: string, label: string);
    selections: string[];
};

export function AggregationItem(props: AggregationItemProps) {
    const { selections, item, onChange } = props;
    const { attribute_code, label, options } = item;

    const onCheckboxChange = useCallback(
        (v) => {
            const checkbox = v.target;
            onChange(checkbox.checked, attribute_code, checkbox.getAttribute('data-value'));
        },
        [attribute_code, onChange],
    );

    const isSwatch = SWATCH_ATTRIBUTE_CODES.has(item.attribute_code);
    const seeMoreTotal = isSwatch ? 20 : 6;
    const hasSelectedValues = options.some((option) => selections.indexOf(option.label!) > -1);

    const [isOpen, toggleFilter] = useState(hasSelectedValues ?? false);
    const [isShowAll, showAll] = useState(false);

    // ----- Removing the Paint Colour Chip - Removal ----
    // ----- Ticket - https://wearejh.atlassian.net/browse/WOOD-1109 ----
    if (attribute_code === 'color' || label === 'Color') {
        return null;
    }

    return (
        <div className={classes.filterOption} data-test-id="category-filter">
            <button
                data-test-id={`category-filter-button-${label}`}
                onClick={() => toggleFilter((isOpen) => !isOpen)}
                className={classnames({
                    [classes.filterOptionToggle]: true,
                    [classes.filterOptionToggleActive]: isOpen,
                })}
                type="button"
            >
                {OPTION_LABELS[attribute_code] || label} <Svg name={isOpen ? 'arrow-up' : 'arrow-down'} />
            </button>

            {isOpen && (
                <div className={classes.filterOptionContent}>
                    <ul
                        className={classnames({
                            [classes.filterOptions]: true,
                            [classes.filterOptionsSwatches]: isSwatch,
                        })}
                    >
                        {options.map((option, index) => {
                            const selected = selections.indexOf(option.label!) > -1;
                            const show = isShowAll ? false : index + 1 > seeMoreTotal;

                            return (
                                <li
                                    key={option.value}
                                    className={classnames({
                                        [classes.filterOptionsItem]: true,
                                        [classes.filterOptionsItemHidden]: show,
                                        [classes.filterOptionsItemSwatch]: isSwatch,
                                    })}
                                >
                                    {isSwatch && (
                                        <Swatch
                                            label={option.displayLabel || ''}
                                            value={option.label || ''}
                                            swatchData={getSwatchData(option)}
                                            selected={selected}
                                            onCheckboxChange={onCheckboxChange}
                                            id={`${item.attribute_code}-${option.value}`}
                                            count={option.count}
                                        />
                                    )}

                                    {!isSwatch && (
                                        <Checkbox
                                            label={option.displayLabel || ''}
                                            value={option.label || ''}
                                            selected={selected}
                                            onCheckboxChange={onCheckboxChange}
                                            id={`${item.attribute_code}-${option.value}`}
                                            count={option.count}
                                        />
                                    )}
                                </li>
                            );
                        })}

                        {options.length > seeMoreTotal && (
                            <li className={classes.filterOptionsMore}>
                                <Button
                                    variant="reset"
                                    className={classes.filterOptionsMoreButton}
                                    onClick={() => showAll(isShowAll ? false : true)}
                                >
                                    {isShowAll ? 'See less' : 'See more'}
                                </Button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

type AggregationInput = {
    count?: number | null;
    id: string;
    label: string;
    onCheckboxChange(v: any);
    selected: boolean;
    swatchData?: SwatchData | undefined;
    value: string;
};

function Checkbox(props: AggregationInput) {
    const { selected, onCheckboxChange, label, id, value, count } = props;

    return (
        <label className={classes.filterOptionsLabel} data-test-id="AggregationItem_Checkbox">
            <input
                data-test-id={`category-filter-option-${value}`}
                type="checkbox"
                checked={selected}
                onChange={onCheckboxChange}
                data-value={value}
                id={id}
                className={classes.filterOptionsCheckbox}
            />
            {label} ({count})
        </label>
    );
}

const Swatch: React.FC<AggregationInput> = React.memo((props) => {
    const { selected, onCheckboxChange, value, id, swatchData, label, count } = props;

    return (
        <label htmlFor={id} className={classes.filterOptionsLabel} data-test-id="AggregationItem_Swatch">
            <input
                type="checkbox"
                checked={selected}
                onChange={onCheckboxChange}
                id={id}
                data-value={value}
                className={classes.filterOptionsCheckbox}
                style={{ backgroundColor: `${value}` }}
            />
            <span className={classes.filterOptionsSwatch} style={swatchData && swatchCss(swatchData)}>
                {' '}
            </span>
            {label} ({count})
        </label>
    );
});
